import * as Sentry from '@sentry/react';

/**
 * Browser-specific messages for a `fetch()` that never completed at the network
 * layer — offline, DNS failure, connection reset, blocked preflight. A failed
 * fetch rejects with a `TypeError` carrying one of these; only HTTP responses
 * (>= 400) become our `ApiError`. So a raw `TypeError` with one of these
 * messages means "the request never reached/returned from the server", i.e. a
 * connectivity problem rather than an application bug.
 */
const NETWORK_ERROR_MESSAGE =
  /failed to fetch|networkerror when attempting to fetch|load failed|network request failed|the network connection was lost/i;

/**
 * True only for a `fetch()`-level connectivity failure — NOT an HTTP error
 * (those are `ApiError`), NOT an intentional abort (`DOMException`/AbortError),
 * and NOT an unrelated `TypeError` (message must match a known fetch-failure).
 */
export function isNetworkError(reason: unknown): boolean {
  // AbortError (cancelled request) is intentional — never treat it as network noise.
  if (reason instanceof DOMException) return false;
  if (!(reason instanceof Error)) return false;
  // Fetch failures surface as TypeError in every engine; gate on the message too
  // so we don't swallow ordinary TypeErrors (undefined access, etc.).
  return reason.name === 'TypeError' && NETWORK_ERROR_MESSAGE.test(reason.message);
}

let installed = false;

/**
 * Install global guards so a background fetch failing while offline doesn't
 * dangle as an unhandled rejection — which trips the dev error overlay and, in
 * production, is just noise. We ONLY neutralize genuine network failures; every
 * other error is left to surface normally so real bugs are never hidden.
 *
 * Suppressed failures are recorded as Sentry **breadcrumbs** (not error events —
 * the `beforeSend` in `index.tsx` also drops any that still reach Sentry as
 * unhandled) so they add offline context to whatever real error might follow,
 * without generating alert noise of their own.
 *
 * Idempotent; safe to call once at bootstrap.
 */
export function installNetworkErrorGuard(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const note = (reason: unknown, source: 'unhandledrejection' | 'error') => {
    const message = reason instanceof Error ? reason.message : String(reason);
    Sentry.addBreadcrumb({
      category: 'network',
      level: 'warning',
      message: `Suppressed offline network error (${source}): ${message}`,
      data: { online: typeof navigator !== 'undefined' ? navigator.onLine : undefined },
    });
  };

  window.addEventListener('unhandledrejection', (event) => {
    if (!isNetworkError(event.reason)) return; // real rejections still surface
    event.preventDefault(); // stop the dangling-rejection log + dev overlay
    note(event.reason, 'unhandledrejection');
  });

  // `error` fires for uncaught runtime errors. A fetch failure normally lands on
  // `unhandledrejection`, not here, but guard defensively — and ONLY for a
  // network TypeError. `event.error` is undefined for resource-load errors, so
  // those (and every real runtime error) fall through untouched.
  window.addEventListener('error', (event) => {
    if (!isNetworkError(event.error)) return;
    event.preventDefault();
    note(event.error, 'error');
  });
}
